import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Divider,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { useAppData } from '../../context/AppDataContext';
import UserService from '../../services/userService';
import { validateImageFile, ALLOWED_IMAGE_EXTENSIONS, MAX_IMAGE_SIZE_MB } from '../../utils/imageValidation';
import { resizeImageForUpload } from '../../utils/imageResize';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import CustomerPageHeader from '../../components/customer/CustomerPageHeader';
import CustomerPageSection from '../../components/customer/CustomerPageSection';
import CustomerPreferencesSections from '../../components/customer/CustomerPreferencesSections';
import CustomerProfilePhoneForm from '../../components/customer/CustomerProfilePhoneForm';
import CustomerProfilePackagesSection from '../../components/customer-packages/CustomerProfilePackagesSection';
import { brandAlpha } from '../../theme/brandTokens';
import { CUSTOMER_COLORS, customerErrorButtonSx, customerOutlinedPrimaryButtonSx } from '../../constants/customerTheme';

const CustomerProfilePage = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { storeId } = useStore();
  const { userPreferences, updateUserProfilePicture } = useAppData();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleLogout = () => {
    const loginUrl = buildCustomerRoute(storeId, 'login') || '/not-found';
    logout();
    navigate(loginUrl);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      const errorMessage = validation.errorParams
        ? t(validation.errorKey, validation.errorParams)
        : t(validation.errorKey);
      setError(errorMessage);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const resized = await resizeImageForUpload(file);
      const response = await UserService.setProfilePicture(resized);
      const pictureUrl = response?.pictureUrl ?? response?.PictureUrl;

      if (pictureUrl && updateUserProfilePicture) {
        updateUserProfilePicture(pictureUrl);
      }

      setSuccess(t('customer.profile.picture.uploadSuccess'));
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError(err.response?.data?.message || t('customer.profile.picture.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);

      await UserService.deleteProfilePicture();

      if (updateUserProfilePicture) {
        updateUserProfilePicture(null);
      }

      setSuccess(t('customer.profile.picture.deleteSuccess'));
    } catch (err) {
      console.error('Error deleting profile picture:', err);
      setError(err.response?.data?.message || t('customer.profile.picture.deleteError'));
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <Box>
      <CustomerPageHeader
        icon={PersonIcon}
        title={t('customer.profile.title')}
        subtitle={t('customer.profile.subtitle')}
      />

      <CustomerProfilePackagesSection storeId={storeId} />

      <CustomerPageSection>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Avatar
            src={userPreferences?.picture}
            sx={{
              width: 120,
              height: 120,
              mb: 2,
              bgcolor: brandAlpha('accent', 0.1),
              border: `3px solid ${brandAlpha('accent', 0.3)}`,
            }}
          >
            <PersonIcon sx={{ fontSize: 60, color: CUSTOMER_COLORS.primary }} />
          </Avatar>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <input
              id="profile-picture-input"
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_IMAGE_EXTENSIONS.join(',')}
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              component="label"
              htmlFor="profile-picture-input"
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              onClick={(e) => {
                e.preventDefault();
                fileInputRef.current?.click();
              }}
              sx={customerOutlinedPrimaryButtonSx}
            >
              {uploading ? t('customer.profile.picture.uploading') : t('customer.profile.picture.upload')}
            </Button>

            {userPreferences?.picture ? (
              <Button
                variant="outlined"
                startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={deleting}
                sx={customerErrorButtonSx}
              >
                {deleting ? t('customer.profile.picture.deleting') : t('customer.profile.picture.delete')}
              </Button>
            ) : null}
          </Box>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        ) : null}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, color: CUSTOMER_COLORS.text, mb: 0.5 }}>
          {user?.name || t('customer.profile.name')}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {user?.email || ''}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <CustomerProfilePhoneForm />

        <Divider sx={{ my: 3 }} />

        <CustomerPreferencesSections />

        <Divider sx={{ my: 3 }} />

        <Button
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            ...customerErrorButtonSx,
            width: '100%',
            height: 44,
            minHeight: 44,
            py: 0,
            fontWeight: 600,
          }}
        >
          {t('customer.menu.logout')}
        </Button>
      </CustomerPageSection>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('common.confirm')}
        message={t('customer.profile.picture.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmColor="error"
      />
    </Box>
  );
};

export default CustomerProfilePage;
